/**
 * loadBVH — the Load stage for Biovision Hierarchy (ADR-008 §2). Format
 * knowledge ends here: downstream conversion consumes the loaded scene graph
 * only, exactly as for GLB/FBX — no per-format branches past this file.
 *
 * BVH is a MESH-LESS animation source (ADR-003 §12): a bare bone hierarchy plus
 * one motion block — no skinned mesh, no inverse-bind matrices. convertRig
 * therefore takes world bind from the loader-resolved rest pose, not IBMs.
 *
 * BVH specifics owned here:
 * - End Sites (leaf tip OFFSETs) are NOT joints. three's BVHLoader materializes
 *   each as a Bone all named 'ENDSITE' — a name collision the RigDescriptor
 *   validator rejects. They are stripped from the scene here; their rest-pose
 *   offsets are surfaced as `endSites` (declared tip hints — tip synthesis,
 *   ADR-010 §12, covers tips via the declared-fraction fallback).
 * - Conventions are UNDECLARED by the format: BVH carries no unit and no
 *   up-axis. Both are declared here with provenance 'spec:bvh-default' (Y-up,
 *   1 unit = 1 m, +Z facing) and are OVERRIDABLE per corpus via options — CMU
 *   is Y-up but scaled, SL/OpenSim BVH is Z-up. A default is a declaration,
 *   never a measurement (ADR-008 §2c); an override stamps 'declared:user'.
 * - Names are verbatim: BVHLoader keeps the authored JOINT names, so there is
 *   no rename to report (authoredNameFor is always undefined).
 * Depends on: three (BVHLoader addon). Depended on by: app/import, fixtures.
 */
import { AnimationClip, Object3D } from 'three';
import { BVHLoader } from 'three/addons/loaders/BVHLoader.js';
import type { Vec3Data } from '@harryhausen/kernel';
import { classifyTake, type TakeClassification } from './load-fbx.js';
import { DegenerateBvhConventionError } from './errors.js';
import type { ConventionDeclaration } from './import-report.js';

/** three's BVHLoader.parse return — a bare skeleton and its single motion clip. */
interface ParsedBvh {
  readonly skeleton: { readonly bones: Object3D[] };
  readonly clip: AnimationClip;
}

/** BVHLoader names every End Site bone this — the marker we strip on (source-hardcoded). */
const END_SITE_NAME = 'ENDSITE';

/** A leaf tip offset (BVH End Site) removed from the skeleton — a declared tip hint. */
export interface BvhEndSite {
  /** The joint whose tip this offset marks. */
  readonly parent: string;
  /** Rest-pose tip offset in the parent joint's local frame. */
  readonly offset: Vec3Data;
}

export interface LoadedBvh {
  readonly scene: Object3D;
  /** BVH carries exactly one motion block — the single clip, classified as loaded. */
  readonly clip: AnimationClip;
  readonly classification: TakeClassification;
  readonly convention: ConventionDeclaration;
  /** BVHLoader keeps authored names verbatim — no rename ever (ADR-008 §2j). */
  readonly authoredNameFor: (object: Object3D) => string | undefined;
  /** BVH has no file metadata — honestly unknown. */
  readonly generator: string | null;
  /** BVH has no extension mechanism — honestly empty, not unchecked. */
  readonly unhandledExtensions: readonly string[];
  /** Leaf tip offsets stripped from the skeleton (End Sites), as declared tip hints. */
  readonly endSites: readonly BvhEndSite[];
}

/** Per-corpus convention overrides — BVH declares none, so the caller may. */
export interface LoadBvhOptions {
  readonly metersPerUnit?: number;
  readonly upAxis?: ConventionDeclaration['upAxis'];
  readonly facing?: ConventionDeclaration['facing'];
  readonly loader?: BVHLoader;
}

const isBone = (node: Object3D): boolean => (node as { isBone?: boolean }).isBone === true;

export function loadBVH(source: string | ArrayBuffer, options: LoadBvhOptions = {}): LoadedBvh {
  const text = typeof source === 'string' ? source : new TextDecoder().decode(source);
  const loader = options.loader ?? new BVHLoader();
  const { skeleton, clip } = loader.parse(text) as ParsedBvh;

  // Assemble the bare-skeleton scene: each root bone (no bone parent) carries
  // its subtree, which BVHLoader has already parented.
  const boneSet = new Set(skeleton.bones);
  const scene = new Object3D();
  for (const b of skeleton.bones) {
    if (b.parent === null || !boneSet.has(b.parent)) scene.add(b);
  }

  // Strip End Sites — declared tip offsets, not joints. Capture each before
  // detaching so the offset survives as a tip hint (ADR-010 §12).
  const endSites: BvhEndSite[] = [];
  const toRemove: Object3D[] = [];
  scene.traverse((node) => {
    if (isBone(node) && node.name === END_SITE_NAME) {
      endSites.push({
        parent: node.parent?.name ?? '(root)',
        offset: { x: node.position.x, y: node.position.y, z: node.position.z },
      });
      toRemove.push(node);
    }
  });
  for (const node of toRemove) node.parent?.remove(node);

  // Convention: BVH declares neither unit nor up-axis. Default loudly; any
  // override re-provenances to the caller (ADR-008 §2c).
  const overridden =
    options.metersPerUnit !== undefined || options.upAxis !== undefined || options.facing !== undefined;
  const convention: ConventionDeclaration = Object.freeze({
    metersPerUnit: options.metersPerUnit ?? 1,
    upAxis: options.upAxis ?? 'Y',
    facing: options.facing ?? '+Z',
    provenance: overridden ? 'declared:user' : 'spec:bvh-default',
  });
  // A facing parallel to up (in the schema, only Z-up + ±Z facing) names no
  // sagittal plane. The default facing is +Z, so a Z-up override MUST also
  // declare a perpendicular facing — refuse rather than default into it.
  if (convention.facing.slice(-1) === convention.upAxis) {
    throw new DegenerateBvhConventionError(convention.upAxis, convention.facing);
  }

  return {
    scene,
    clip,
    classification: classifyTake(clip),
    convention,
    authoredNameFor: () => undefined,
    generator: null,
    unhandledExtensions: [],
    endSites,
  };
}
